<template>
  <div>
    <p v-if="this.produit.supportCommercial" class="has-text-weight-semibold">
      {{ getInfo()}}
    </p>
    <div class="columns is-vcentered mgt-10px">
      <div class="column is-narrow">
        <p class="label">{{ $t('ajoutSalonDcf.liaisonTarif.dateVisualisation') }}</p>
      </div>
      <div class="column is-narrow">
        <b-datepicker
          show-week-number
          nearby-selectable-month-days
          v-model="dateVisualisation"
          icon="calendar-today"
          :editable="true">
        </b-datepicker>
      </div>
    </div>
    <v-client-table
      :data="getLiaisonTarifFiltered"
      :columns="columns"
      :options="options"
      ref="tableLiaisontarif"
      class="mgt-20px">
      <template slot-scope="props" slot="selected">
        <div class="has-text-centered">
          <b-checkbox
            :value="props.row.selected"
            class="mgt-5px mgl-5px"
            @input="updateValue(props.row.id, 'selected', $event)">
          </b-checkbox>
        </div>
      </template>
      <template slot-scope="props" slot="libelle">
        <div v-if="!isEncart" v-html="formatBackToLineHtml(props.row.libelle)"></div>
        <b-tooltip
          v-else
          position="is-bottom"
          multilined>
          <div
            class="max250px min150px ellipsis"
            v-html="formatBackToLineHtml(props.row.libelle)">
          </div>
          <template v-slot:content>
            <div v-html="formatBackToLineHtml(props.row.libelle)"></div>
          </template>
        </b-tooltip>
      </template>
      <template slot-scope="props" slot="libelleComplementaire">
        <div v-if="!isEncart" v-html="props.row.libelleComplementaire"></div>
        <b-tooltip
          v-else
          position="is-bottom"
          multilined>
          <div class="max250px min150px ellipsis" v-html="props.row.libelleComplementaire"></div>
          <template v-slot:content>
            <div v-html="props.row.libelleComplementaire"></div>
          </template>
        </b-tooltip>
      </template>
      <template slot-scope="props" slot="quantite">
        <span v-if="props.row.quantite">
          {{props.row.quantite }} {{ $t(findUniteVente(props.row.uniteVente)) }}
        </span>
      </template>
      <template slot-scope="props" slot="prixUnitaire">
        {{ props.row.prixUnitaire | formatDevise }}
      </template>
      <template slot-scope="props" slot="dateDebut">
        {{ props.row.dateDebut | formatDate }}
      </template>
      <template slot-scope="props" slot="dateFin">
        {{ props.row.dateFin | formatDate }}
      </template>
    </v-client-table>
  </div>
</template>

<script>
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_POP_UP,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import { LISTE_UNITES } from '@/configuration/devisConf';
import { CATEGORIE_SALON, CATEGORIE_SPONSORING, CATEGORIE_PRESSE } from '@/configuration/catalogue/produitConf';
import { columnsSplice } from '@/services/general/columns';
import formatBackToLineHtml from '@/services/general/formatBackToLineHtml';

export default {
  name: 'liaisonsTarif',
  props: {
    liaisonsTarif: {
      type: Array,
      required: true,
    },
    produit: {
      type: Object,
      required: true,
    },
    isEncart: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dateVisualisation: new Date(),
      formatBackToLineHtml,
    };
  },
  computed: {
    getLiaisonTarifFiltered() {
      return this.liaisonsTarif
        .filter((liaison) => {
          return liaison.actif === undefined || liaison.actif;
        }).filter((liaison) => {
          return !this.dateVisualisation || !liaison.dateDebut
            || (!liaison.dateFin && moment(this.dateVisualisation)
              .isSameOrAfter(moment(liaison.dateDebut)))
            || moment(this.dateVisualisation).isBetween(moment(liaison.dateDebut),
              moment(liaison.dateFin), 'day', '[]');
        });
    },
    columns() {
      const columns = ['selected', 'libelle', 'libelleComplementaire', 'quantite', 'prixUnitaire', 'dateDebut', 'dateFin'];
      if (!this.isEncart) columnsSplice(columns, 'libelleComplementaire');
      return columns;
    },
    filtrable() {
      const cols = ['libelle', 'libelleComplementaire', 'quantite', 'prixUnitaire'];
      if (!this.isEncart) columnsSplice(cols, 'libelleComplementaire');
      return cols;
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelle: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.libelle'),
          libelleComplementaire: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.libelleComplementaire'),
          quantite: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.quantite'),
          dateDebut: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.dateDebut'),
          dateFin: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.dateFin'),
          prixUnitaire: this.$i18n.t('ajoutSalonDcf.liaisonTarif.tab.prixUnitaire'),
          selected: '',
        },
        filterable: this.filtrable,
        filterByColumn: true,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_POP_UP,
        texts: texts,
        columnsClasses: {
          selected: 'colonneSelected',
          libelle: 'colonneWidth250px',
          libelleComplementaire: 'colonneWidth250px',
        },
        sortable: ['libelle', 'libelleComplementaire', 'dateDebut', 'dateFin', 'quantite', 'prixUnitaire'],
      };
    },
  },
  methods: {
    findUniteVente(unite) {
      const uniteVente = LISTE_UNITES.find((typeRemise) => {
        return typeRemise.code === unite;
      });
      if (uniteVente) return uniteVente.libelleLight;
      return '';
    },
    updateValue(id, variable, value) {
      const liaisonFind = this.liaisonsTarif.find((el) => el.id === id);
      if (this.isEncart || liaisonFind.uniqueSelection) {
        this.liaisonsTarif.forEach((el) => {
          el.selected = false;
        });
      }
      if (liaisonFind) {
        liaisonFind[variable] = value;
      }
    },
    getInfo() {
      switch (this.produit.supportCommercial.categorieVente) {
        case CATEGORIE_SALON:
        case CATEGORIE_SPONSORING:
          return this.$t('ajoutSalonDcf.liaisonTarif.infoPrestation', { produit: this.produit.libelle });
        case CATEGORIE_PRESSE:
          return this.$t('ajoutSalonDcf.liaisonTarif.infoEncart', { produit: this.produit.libelle });
        default:
          return this.$t('ajoutSalonDcf.liaisonTarif.info', { produit: this.produit.libelle });
      }
    },
  },
};
</script>

<style scoped>

</style>
